import { NgClass, NgIf, DatePipe, SlicePipe, NgFor, TitleCasePipe } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    ChangeDetectorRef,
    inject
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { CourtsService } from './courts.service';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { EditCourtDialogComponent } from './edit-court-dialog/edit-court-dialog.component';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { AuthService } from 'app/core/auth/auth.service';
import { getSportDisplayName, sportMatchesQuery } from 'app/shared/sport-display';

@Component({
    selector: 'app-courts',
    templateUrl: './courts.component.html',
    styleUrl: './courts.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIconModule,
        NgClass,
        NgIf,
        NgFor,
        DatePipe,
        SlicePipe,
        TitleCasePipe,
        FormsModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatPaginatorModule,
        MatMenuModule,
        MatDialogModule
    ],
})
export class CourtsComponent implements OnInit, OnDestroy {
    recentCourtsDataSource: MatTableDataSource<any> = new MatTableDataSource();
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    
    // Search properties
    searchQuery: string = '';
    filteredCourts: any[] = [];
    showSearchDropdown: boolean = false;
    isSearchActive: boolean = false;
    selectedCourt: any = null;
    
    // Pagination properties
    pageSize: number = 10; // Show 10 items per page in table
    currentPage: number = 0;
    totalCourts: number = 0;
    pageSizeOptions: number[] = [10, 25, 50, 100];
    
    // Original courts from API
    allCourts: any[] = [];
    
    // Stats properties
    activeCourtsCount: number = 0;
    inactiveCourtsCount: number = 0;
    outdoorCourtsCount: number = 0;
    indoorCourtsCount: number = 0;
    
    // Expanded rows
    expandedCourtId: string | null = null;
    
    readonly dialog = inject(MatDialog);
    private readonly authService = inject(AuthService);
    readonly canPerformDestructiveActions = this.authService.isSuperAdmin();

    constructor(
        private courtsService: CourtsService,
        private cdr: ChangeDetectorRef,
        private snackBar: MatSnackBar,
        private _fuseConfirmationService: FuseConfirmationService,
    ) { }

    ngOnInit(): void {
        this.getAllCourts();
    }

    getAllCourts(): void { 
        this.courtsService.getAllCourtsforsuperadmin()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (response) => {
                    // Extract the data array from the response
                    const courtsArray = response?.data || [];
                    
                    if (!Array.isArray(courtsArray)) {
                        console.error('Response data is not an array:', courtsArray);
                        this.snackBar.open('Invalid data format from server', 'Close', { duration: 3000 });
                        this.cdr.markForCheck();
                        return;
                    }
                    
                    if (courtsArray.length === 0) {
                        this.allCourts = [];
                        this.recentCourtsDataSource.data = [];
                        this.calculateStats();
                        this.updateDisplayedCourts();
                        this.cdr.markForCheck();
                        return;
                    }
                    
                    // Create array of observables to fetch sports logos
                    const sportLogoRequests = courtsArray.map(court => 
                        this.courtsService.getSportByName(court.sports).pipe(
                            takeUntil(this._unsubscribeAll)
                        )
                    );
                    
                    // Fetch all sports logos in parallel
                    forkJoin(sportLogoRequests).subscribe({
                        next: (sportResponses) => {
                            // Map courts with their corresponding sports logos and company name
                            this.allCourts = courtsArray.map((court, index) => {
                                const sportData = sportResponses[index];
                                const sportLogo = sportData?.logo || '';
                                const sportColor = sportData?.color || '#6B7280';
                                const companyName = court.venueId?.companyId?.name || 'Unknown Company';
                                
                                return {
                                    id: court._id,
                                    _id: court._id,
                                    name: court.name,
                                    sports: court.sports || '',
                                    sportLogo: sportLogo,
                                    sportColor: sportColor,
                                    sportName: getSportDisplayName(sportData?.name || court.sports),
                                    status: court.status,
                                    courtType: court.courtType,
                                    surfaceType: court.surfaceType,
                                    airConditioned: court.airConditioned,
                                    minCapacity: court.minCapacity,
                                    maxCapacity: court.maxCapacity,
                                    pricePerHour: court.pricePerHour,
                                    pictures: court.pictures || [],
                                    venueId: court.venueId?._id || null,
                                    venueName: court.venueId?.name || 'Unknown Venue',
                                    companyId: court.companyId,
                                    companyName: companyName, // Added company name
                                    currency: court.currency || 'AED',
                                    subtotalPerHour: court.subtotalPerHour,
                                    taxPerHour: court.taxPerHour,
                                    total: court.total,
                                    createdBy: court.createdBy,
                                    createdAt: court.createdAt,
                                    updatedAt: court.updatedAt
                                };
                            });
                            
                            this.calculateStats();
                            this.filteredCourts = [...this.allCourts];
                            this.totalCourts = this.allCourts.length;
                            this.updateDisplayedCourts();
                            this.cdr.markForCheck();
                        },
                        error: (err) => {
                            console.error('Error fetching sports logos:', err);
                            // Fallback: map courts without logos
                            this.mapCourtsWithoutLogos(courtsArray);
                        }
                    });
                },
                error: (err) => {
                    console.error('Error fetching courts:', err);
                    this.snackBar.open('Error loading courts', 'Close', { duration: 3000 });
                    this.cdr.markForCheck();
                }
            });
    }
    
    // Fallback method if sports logos fail to load
    private mapCourtsWithoutLogos(courtsArray: any[]): void {
        this.allCourts = courtsArray.map(court => {
            const companyName = court.venueId?.companyId?.name || 'Unknown Company';
            
            return {
                id: court._id,
                _id: court._id,
                name: court.name,
                sports: court.sports || '',
                sportLogo: '',
                sportColor: '#6B7280',
                sportName: getSportDisplayName(court.sports),
                status: court.status,
                courtType: court.courtType,
                surfaceType: court.surfaceType,
                airConditioned: court.airConditioned,
                minCapacity: court.minCapacity,
                maxCapacity: court.maxCapacity,
                pricePerHour: court.pricePerHour,
                pictures: court.pictures || [],
                venueId: court.venueId?._id || null,
                venueName: court.venueId?.name || 'Unknown Venue',
                companyId: court.companyId,
                companyName: companyName, // Added company name
                currency: court.currency || 'AED',
                subtotalPerHour: court.subtotalPerHour,
                taxPerHour: court.taxPerHour,
                total: court.total,
                createdBy: court.createdBy,
                createdAt: court.createdAt,
                updatedAt: court.updatedAt
            };
        });
        
        this.calculateStats();
        this.filteredCourts = [...this.allCourts];
        this.totalCourts = this.allCourts.length;
        this.updateDisplayedCourts();
        this.cdr.markForCheck();
    }

    // Calculate stats
    calculateStats(): void {
        this.activeCourtsCount = this.allCourts.filter(c => c.status === 'active').length;
        this.inactiveCourtsCount = this.allCourts.filter(c => c.status === 'inactive').length;
        this.outdoorCourtsCount = this.allCourts.filter(c => c.courtType === 'outdoor').length;
        this.indoorCourtsCount = this.allCourts.filter(c => c.courtType === 'indoor').length;
    }

    // Search functionality
    onSearchInput(): void {
        if (this.searchQuery.trim() === '') {
            this.clearSearch();
            return;
        }

        const query = this.searchQuery.toLowerCase();
        this.filteredCourts = this.allCourts.filter(court => 
            court.name.toLowerCase().includes(query) || 
            (court.venueName?.toLowerCase() || '').includes(query) ||
            (court.companyName?.toLowerCase() || '').includes(query) || // Search by company name
            sportMatchesQuery(court.sports || court.sportName, query) ||
            (court.status?.toLowerCase() || '').includes(query) ||
            (court.courtType?.toLowerCase() || '').includes(query) ||
            (court.surfaceType?.toLowerCase() || '').includes(query)
        );
        
        this.showSearchDropdown = this.filteredCourts.length > 0;
        this.isSearchActive = false;
        this.selectedCourt = null;
        this.expandedCourtId = null;
        
        // Update pagination for search results
        this.currentPage = 0;
        this.totalCourts = this.filteredCourts.length;
        this.updateDisplayedCourts();
        
        this.cdr.markForCheck();
    }

    selectCourt(court: any): void {
        this.selectedCourt = court;
        this.searchQuery = court.name;
        this.showSearchDropdown = false;
        this.isSearchActive = true;
        this.expandedCourtId = court._id;
        
        // Filter to show only selected court
        this.filteredCourts = [court];
        this.currentPage = 0;
        this.totalCourts = 1;
        this.updateDisplayedCourts();
        
        // Scroll to the selected row
        setTimeout(() => {
            const element = document.getElementById(`court-${court._id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                element.classList.add('bg-yellow-50');
                setTimeout(() => element.classList.remove('bg-yellow-50'), 2000);
            }
        }, 100);
        
        this.cdr.markForCheck();
    }

    clearSearch(): void {
        this.searchQuery = '';
        this.selectedCourt = null;
        this.showSearchDropdown = false;
        this.isSearchActive = false;
        this.expandedCourtId = null;
        
        // Reset to show all courts
        this.filteredCourts = [...this.allCourts];
        this.currentPage = 0;
        this.totalCourts = this.allCourts.length;
        this.updateDisplayedCourts();
        
        this.cdr.markForCheck();
    }

    onSearchBlur(): void {
        setTimeout(() => {
            this.showSearchDropdown = false;
            this.cdr.markForCheck();
        }, 200);
    }

    // Toggle row expansion
    toggleRow(courtId: string): void {
        if (this.expandedCourtId === courtId) {
            this.expandedCourtId = null;
        } else {
            this.expandedCourtId = courtId;
        }
        this.cdr.markForCheck();
    }

    // Pagination methods
    updateDisplayedCourts(): void {
        const startIndex = this.currentPage * this.pageSize;
        const endIndex = startIndex + this.pageSize;
        this.recentCourtsDataSource.data = this.filteredCourts.slice(startIndex, endIndex);
    }

    onPageChange(event: PageEvent): void {
        this.currentPage = event.pageIndex;
        this.pageSize = event.pageSize;
        this.expandedCourtId = null;
        this.updateDisplayedCourts();
        this.cdr.markForCheck();
    }

    getPageInfo(): string {
        const start = (this.currentPage * this.pageSize) + 1;
        const end = Math.min((this.currentPage + 1) * this.pageSize, this.totalCourts);
        return `Showing ${start}-${end} of ${this.totalCourts} courts`;
    }

    refreshCourts(): void {
        this.clearSearch();
        this.getAllCourts();
        this.snackBar.open('Courts refreshed', 'Close', { duration: 2000 });
    }

    // Edit court method
    editCourt(court: any, event: Event): void {
        event.stopPropagation(); // Prevent row expansion
        
        const dialogRef = this.dialog.open(EditCourtDialogComponent, {
            width: '1000px',
            maxWidth: '1000px',
            maxHeight: '90vh',
            data: { court },
            disableClose: false,
            autoFocus: false,
            panelClass: 'edit-court-dialog-container'
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // Refresh courts after successful update
                this.getAllCourts();
            }
        });
    }

    // Delete court method - NOW USING SUPER ADMIN API
    deleteCourt(courtId: string, venueId: string, event: Event): void {
        event.stopPropagation();
        
        const confirmation = this._fuseConfirmationService.open({
            title: 'Delete Court',
            message: 'Are you sure you want to delete this court? This action cannot be undone.',
            actions: {
                confirm: {
                    label: 'Delete',
                    color: 'warn'
                },
                cancel: {
                    label: 'Cancel'
                }
            }
        });

        confirmation.afterClosed().subscribe(result => {
            if (result === 'confirmed') {
                // CHANGED: Now using deleteCourtBySuperAdmin instead of regular delete
                this.courtsService.deleteCourtBySuperAdmin(venueId, courtId)
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: () => {
                            // Remove court from local arrays
                            this.allCourts = this.allCourts.filter(c => c._id !== courtId);
                            this.filteredCourts = this.filteredCourts.filter(c => c._id !== courtId);
                            this.updateDisplayedCourts();
                            this.calculateStats();
                            
                            this.snackBar.open('Court deleted successfully', 'Close', { duration: 3000 });
                            this.cdr.markForCheck();
                        },
                        error: (err) => {
                            console.error('Error deleting court:', err);
                            this.snackBar.open('Error deleting court', 'Close', { duration: 3000 });
                            this.cdr.markForCheck();
                        }
                    });
            }
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    /**
     * Track by function for ngFor loops
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
}
